// ────────────────────────────────────────────────────────────────────
// A tag-shaped token from a filename — the guess that prefills the
// file-to-asset picker's search box.
//
// This is the same "ไม่เดา" bargain the import console makes: prefill only when
// the filename actually names an asset, and stay silent otherwise. A wrong
// guess is not neutral — the picker searches it, finds nothing, and the user
// has to clear the box before they can type. So both rules below exist to make
// the function return "" more often, not to make it match more often.
// ────────────────────────────────────────────────────────────────────

/** A whole token that reads as an asset tag: V-101, T2401, E-101A. */
const TAG_TOKEN = /^[A-Za-z]{1,5}-?\d{1,4}[A-Za-z]?$/;

/** Everything that is not part of a tag separates tokens. Hyphens are NOT
 *  separators — they are inside the tags we are looking for — which is why a
 *  filename joined by hyphens ("UT-survey-2026-01") yields ONE token that
 *  fails TAG_TOKEN, rather than a fragment cut out of "survey". */
const SEPARATOR = /[^A-Za-z0-9-]+/;

/** Letter prefixes that fit the tag shape but never name an asset: NDT methods
 *  and the words that show up in document filenames. "P" (pump) and "T" (tank)
 *  are deliberately absent — those ARE asset prefixes. */
const NOT_AN_ASSET = new Set([
  "ut", "rt", "mt", "pt", "vt", "et", "ae", "paut", "tofd", "tfm", "pmi",
  "rvi", "gvi", "ndt", "acfm", "mfl",
  "rev", "doc", "rpt", "fig", "img", "dsc", "scan", "page", "iso", "asme", "api",
]);

/**
 * The first token of `filename` that reads as an asset tag, or "" when the
 * filename does not name one.
 *
 * @example guessTag("V-101_UT_2024.xlsx")   // "V-101"
 * @example guessTag("UT-survey-2026-01.csv") // ""  — no token is a tag
 */
export function guessTag(filename: string): string {
  const base = filename.replace(/\.[^.]+$/, "");
  for (const token of base.split(SEPARATOR)) {
    if (!TAG_TOKEN.test(token)) continue;
    const prefix = token.match(/^[A-Za-z]+/)?.[0].toLowerCase() ?? "";
    if (NOT_AN_ASSET.has(prefix)) continue;
    return token;
  }
  return "";
}
